import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import { useForm, useForms } from './useForms';
import { useAuth } from './useAuth';
import { useToast } from '../components/ui/Toast';
import { logService } from '../lib/logService';
import { createDefaultField, generateId } from '../lib/fieldPalette';
import { exportFormToMarkdown, copyMarkdownToClipboard, downloadMarkdownFile } from '../lib/markdown/markdownExporter';
import type { FormField, FieldType, FieldAlignment, Form, FormSettings, FormPage, ThemeConfig, FieldRule } from '../types/forms';
import type { FieldOptionScore } from '../types/forms';
import { DEFAULT_THEME_CONFIG, PAGE_EXCLUDED_FIELD_TYPES } from '../types/forms';
import { createPage as createFormPage } from '../lib/formPages';
import type { ParseResult } from '../lib/markdown/markdownParser';
import type { SaveResult } from '../lib/builderTelemetry';

// =====================================================
// UNDO/REDO — Immutable History Stack (ADR-SE-004)
// =====================================================

interface EditorSnapshot {
  fields: FormField[];
  pages: FormPage[];
  title: string;
  description: string;
  settings: FormSettings;
}

const MAX_HISTORY = 50;

// =====================================================
// RETURN TYPE — matches architecture doc section 3.2
// =====================================================

export interface UseSlideEditorReturn {
  // Data
  fields: FormField[];
  pages: FormPage[];
  title: string;
  description: string;
  settings: FormSettings;
  themeConfig: ThemeConfig;
  selectedFieldId: string | null;
  selectedField: FormField | null;
  selectedPageId: string | null;
  selectedPage: FormPage | null;
  user: ReturnType<typeof useAuth>['user'];
  isPublished: boolean;

  // Loading / error
  loading: boolean;
  error: string | null;

  // State
  hasChanges: boolean;
  isSaving: boolean;
  isPlayMode: boolean;
  viewportMode: 'desktop' | 'mobile';
  canUndo: boolean;
  canRedo: boolean;
  blocker: ReturnType<typeof useBlocker>;

  // UI visibility
  showAddFieldModal: boolean;
  showThemePanel: boolean;
  showSettingsModal: boolean;

  // Field CRUD
  addField: (type: FieldType) => string;
  addFieldsBatch: (labels: string[]) => void;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
  updateFieldConfig: (fieldId: string, config: Record<string, any>) => void;
  updateOptionScore: (fieldId: string, optIdx: number, varId: string, pts: number) => void;
  updateFieldRules: (fieldId: string, rules: FieldRule[]) => void;
  deleteField: (fieldId: string) => void;
  duplicateField: (fieldId: string) => void;
  reorderFields: (fromIndex: number, toIndex: number) => void;
  setAlignmentAll: (alignment: FieldAlignment) => void;
  selectField: (fieldId: string | null) => void;
  selectPage: (pageId: string | null) => void;

  // Page CRUD (Story 5.2)
  addPage: (title?: string) => FormPage;
  deletePage: (pageId: string) => void;
  updatePage: (pageId: string, updates: Partial<FormPage>) => void;
  reorderPages: (fromIndex: number, toIndex: number) => void;
  moveFieldToPage: (fieldId: string, pageId: string | null) => void;

  // Form actions
  setTitle: (title: string) => void;
  setDescription: (desc: string) => void;
  updateSettings: (update: Partial<FormSettings>) => void;
  save: () => Promise<SaveResult>;
  publish: () => Promise<void>;
  exit: () => void;

  // Editor UI
  undo: () => void;
  redo: () => void;
  togglePlayMode: () => void;
  toggleViewport: () => void;
  openAddFieldModal: () => void;
  closeAddFieldModal: () => void;
  toggleThemePanel: () => void;
  openSlideSettings: () => void;
  closeSlideSettings: () => void;
  formSnapshot: Form;
  buildFormSnapshot: () => Form;

  // Markdown
  handleMarkdownImport: (result: ParseResult, mode: 'replace' | 'append') => void;
  handleExportMarkdown: () => void;
  handleCopyMarkdown: () => Promise<void>;
}

// =====================================================
// HOOK IMPLEMENTATION
// =====================================================

export function useSlideEditor(formId?: string): UseSlideEditorReturn {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();

  // Load existing form (if editing)
  const { form: serverForm, loading, error, updateForm } = useForm(formId);
  const { createForm } = useForms();

  // ── Local state ──────────────────────────────────────
  const [title, setTitleState] = useState('Novo Formulário');
  const [description, setDescriptionState] = useState('');
  const [fields, setFields] = useState<FormField[]>([]);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [pages, setPages] = useState<FormPage[]>([]);
  const [localSettings, setLocalSettings] = useState<FormSettings>({});
  const [isSaving, setIsSaving] = useState(false);

  // ── UI visibility state ──────────────────────────────
  const [isPlayMode, setIsPlayMode] = useState(false);
  const [viewportMode, setViewportMode] = useState<'desktop' | 'mobile'>('desktop');
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [showThemePanel, setShowThemePanel] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  // ── Undo/Redo stacks ─────────────────────────────────
  const [undoStack, setUndoStack] = useState<EditorSnapshot[]>([]);
  const [redoStack, setRedoStack] = useState<EditorSnapshot[]>([]);

  // ── Sync from server ─────────────────────────────────
  useEffect(() => {
    if (serverForm) {
      setTitleState(serverForm.title);
      setDescriptionState(serverForm.description || '');
      setFields(serverForm.fields || []);
      setPages(serverForm.pages || []);
      setLocalSettings(serverForm.settings || {});
    }
  }, [serverForm]);

  // ── Auto-select: always keep something selected when fields exist ──
  useEffect(() => {
    if (fields.length > 0 && !selectedFieldId) {
      setSelectedFieldId(fields[0].id);
    }
  }, [fields, selectedFieldId]);

  // ── Derived values ───────────────────────────────────
  const isEditMode = !!formId;
  const isPublished = serverForm?.is_published ?? false;
  const themeConfig: ThemeConfig = localSettings?.themeConfig || DEFAULT_THEME_CONFIG;

  const selectedField = useMemo(
    () => fields.find(f => f.id === selectedFieldId) || null,
    [fields, selectedFieldId]
  );

  const selectedPage = useMemo(
    () => pages.find(p => p.id === selectedPageId) || null,
    [pages, selectedPageId]
  );

  // ── hasChanges detection ─────────────────────────────
  const hasChanges = useMemo(() => {
    if (serverForm) {
      const hasFieldChanges = JSON.stringify(fields) !== JSON.stringify(serverForm.fields);
      const hasPagesChange = JSON.stringify(pages) !== JSON.stringify(serverForm.pages || []);
      const hasTitleChange = title !== serverForm.title;
      const hasDescChange = description !== (serverForm.description || '');
      const hasSettingsChange = JSON.stringify(localSettings) !== JSON.stringify(serverForm.settings || {});
      return hasFieldChanges || hasPagesChange || hasTitleChange || hasDescChange || hasSettingsChange;
    } else if (!isEditMode) {
      return title !== 'Novo Formulário' || fields.length > 0 || pages.length > 0 || description !== '';
    }
    return false;
  }, [serverForm, fields, pages, title, description, localSettings, isEditMode]);

  // ── beforeunload guard ───────────────────────────────
  useEffect(() => {
    if (!hasChanges) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  // ── useBlocker for in-app navigation ─────────────────
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      hasChanges && currentLocation.pathname !== nextLocation.pathname
  );

  // ── Undo/Redo helpers (two-stack model) ─────────────
  const pushSnapshot = useCallback(() => {
    const snapshot: EditorSnapshot = {
      fields: structuredClone(fields),
      pages: structuredClone(pages),
      title,
      description,
      settings: { ...localSettings },
    };
    setUndoStack(prev => {
      const next = [...prev, snapshot];
      if (next.length > MAX_HISTORY) next.shift();
      return next;
    });
    setRedoStack([]);
  }, [fields, pages, title, description, localSettings]);

  const undo = useCallback(() => {
    if (undoStack.length === 0) return;
    // Push current live state onto redo stack
    const liveSnapshot: EditorSnapshot = {
      fields: structuredClone(fields),
      pages: structuredClone(pages),
      title,
      description,
      settings: { ...localSettings },
    };
    setRedoStack(prev => [...prev, liveSnapshot]);
    // Pop undo stack and restore
    const snapshot = undoStack[undoStack.length - 1];
    setUndoStack(prev => prev.slice(0, -1));
    setFields(snapshot.fields);
    setPages(snapshot.pages);
    setTitleState(snapshot.title);
    setDescriptionState(snapshot.description);
    setLocalSettings(snapshot.settings);
  }, [undoStack, fields, pages, title, description, localSettings]);

  const redo = useCallback(() => {
    if (redoStack.length === 0) return;
    // Push current live state onto undo stack
    const liveSnapshot: EditorSnapshot = {
      fields: structuredClone(fields),
      pages: structuredClone(pages),
      title,
      description,
      settings: { ...localSettings },
    };
    setUndoStack(prev => [...prev, liveSnapshot]);
    // Pop redo stack and restore
    const snapshot = redoStack[redoStack.length - 1];
    setRedoStack(prev => prev.slice(0, -1));
    setFields(snapshot.fields);
    setPages(snapshot.pages);
    setTitleState(snapshot.title);
    setDescriptionState(snapshot.description);
    setLocalSettings(snapshot.settings);
  }, [redoStack, fields, pages, title, description, localSettings]);

  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  // ── Keyboard shortcuts (Ctrl+Z / Ctrl+Shift+Z) ──────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.key === 'z' && e.shiftKey) || e.key === 'Z') {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo]);

  // ── Setters (with snapshot) ──────────────────────────
  const setTitle = useCallback((newTitle: string) => {
    pushSnapshot();
    setTitleState(newTitle);
  }, [pushSnapshot]);

  const setDescription = useCallback((newDesc: string) => {
    pushSnapshot();
    setDescriptionState(newDesc);
  }, [pushSnapshot]);

  const updateSettings = useCallback((update: Partial<FormSettings>) => {
    pushSnapshot();
    setLocalSettings(prev => ({ ...prev, ...update }));
  }, [pushSnapshot]);

  // ── Field CRUD ───────────────────────────────────────
  const addField = useCallback((type: FieldType): string => {
    pushSnapshot();
    const newField = createDefaultField(type);
    setFields(prev => [...prev, newField]);
    setSelectedFieldId(newField.id);
    return newField.id;
  }, [pushSnapshot]);

  const addFieldsBatch = useCallback((labels: string[]) => {
    if (labels.length === 0) return;
    pushSnapshot(); // single snapshot → 1x undo reverses entire batch
    const newFields = labels.map(label => {
      const field = createDefaultField('text' as FieldType);
      field.label = label;
      return field;
    });
    setFields(prev => [...prev, ...newFields]);
    setSelectedFieldId(newFields[0].id);
    showToast('success', `${newFields.length} pergunta${newFields.length !== 1 ? 's' : ''} importada${newFields.length !== 1 ? 's' : ''}`);
  }, [pushSnapshot, showToast]);

  const updateField = useCallback((fieldId: string, updates: Partial<FormField>) => {
    pushSnapshot();
    setFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  }, [pushSnapshot]);

  const updateFieldConfig = useCallback((fieldId: string, configUpdate: Record<string, any>) => {
    pushSnapshot();
    setFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      return { ...f, config: { ...(f.config || {}), ...configUpdate } as any };
    }));
  }, [pushSnapshot]);

  const updateOptionScore = useCallback((
    fieldId: string,
    optionIndex: number,
    variableId: string,
    points: number
  ) => {
    pushSnapshot();
    setFields(prev => prev.map(f => {
      if (f.id !== fieldId || !f.options) return f;
      const newOptions = f.options.map((opt, idx) => {
        if (idx !== optionIndex) return opt;
        const normalized = typeof opt === 'string'
          ? { label: opt, value: opt, scores: [] as FieldOptionScore[] }
          : { ...opt, scores: [...(opt.scores || [])] };

        const existingIdx = normalized.scores.findIndex(s => s.variableId === variableId);
        if (existingIdx >= 0) {
          normalized.scores[existingIdx] = { variableId, points };
        } else {
          normalized.scores.push({ variableId, points });
        }
        normalized.scores = normalized.scores.filter(s => s.points !== 0);
        return normalized;
      });
      return { ...f, options: newOptions };
    }));
  }, [pushSnapshot]);

  const updateFieldRules = useCallback((fieldId: string, rules: FieldRule[]) => {
    pushSnapshot();
    setFields(prev => prev.map(f =>
      f.id === fieldId ? { ...f, rules } : f
    ));
  }, [pushSnapshot]);

  const deleteField = useCallback((fieldId: string) => {
    pushSnapshot();
    setFields(prev => {
      const deletedIndex = prev.findIndex(f => f.id === fieldId);
      const newFields = prev.filter(f => f.id !== fieldId);
      // Auto-select the nearest remaining field
      if (selectedFieldId === fieldId) {
        const nextField = newFields[Math.min(deletedIndex, newFields.length - 1)];
        setSelectedFieldId(nextField?.id || null);
      }
      return newFields;
    });
  }, [pushSnapshot, selectedFieldId]);

  const duplicateField = useCallback((fieldId: string) => {
    pushSnapshot();
    const newId = generateId();
    setFields(prev => {
      const fieldToDuplicate = prev.find(f => f.id === fieldId);
      if (!fieldToDuplicate) return prev;
      const newField: FormField = {
        ...fieldToDuplicate,
        id: newId,
        label: `${fieldToDuplicate.label} (cópia)`,
      };
      const index = prev.findIndex(f => f.id === fieldId);
      const newFields = [...prev];
      newFields.splice(index + 1, 0, newField);
      return newFields;
    });
    setSelectedFieldId(newId);
  }, [pushSnapshot]);

  const reorderFields = useCallback((fromIndex: number, toIndex: number) => {
    pushSnapshot();
    setFields(prev => {
      const newFields = [...prev];
      const [movedField] = newFields.splice(fromIndex, 1);
      newFields.splice(toIndex, 0, movedField);
      return newFields;
    });
  }, [pushSnapshot]);

  // mobile-ui.6.F F4 — alinhamento em massa. UM pushSnapshot → 1 undo
  // reverte o lote inteiro. PAGE_EXCLUDED (welcome/thank-you/redirect) não
  // são "perguntas": ficam intactos e mantêm o default type-aware de
  // getEffectiveAlignment (center nas special screens).
  const setAlignmentAll = useCallback((alignment: FieldAlignment) => {
    pushSnapshot();
    setFields(prev => prev.map(f =>
      PAGE_EXCLUDED_FIELD_TYPES.includes(f.type) ? f : { ...f, alignment }
    ));
  }, [pushSnapshot]);

  const selectField = useCallback((fieldId: string | null) => {
    setSelectedFieldId(fieldId);
    if (fieldId) setSelectedPageId(null);
  }, []);

  const selectPage = useCallback((pageId: string | null) => {
    setSelectedPageId(pageId);
    if (pageId) setSelectedFieldId(null);
  }, []);

  // ── Page CRUD (Story 5.2) ─────────────────────────────
  const addPage = useCallback((title?: string) => {
    pushSnapshot();
    const maxOrder = pages.length > 0 ? Math.max(...pages.map(p => p.order)) : -1;
    const newPage = createFormPage(title ?? `Página ${pages.length + 1}`);
    newPage.order = maxOrder + 1;
    setPages(prev => [...prev, newPage]);
    return newPage;
  }, [pushSnapshot, pages]);

  const deletePage = useCallback((pageId: string) => {
    pushSnapshot();
    const pageIndex = pages.findIndex(p => p.id === pageId);
    const previousPage = pageIndex > 0 ? pages[pageIndex - 1] : null;

    // Move fields to previous page, or remove pageId if no previous page
    setFields(prev => prev.map(f => {
      if (f.pageId !== pageId) return f;
      return { ...f, pageId: previousPage?.id ?? undefined };
    }));

    setPages(prev => prev.filter(p => p.id !== pageId));
  }, [pushSnapshot, pages]);

  const updatePage = useCallback((pageId: string, updates: Partial<FormPage>) => {
    pushSnapshot();
    setPages(prev => prev.map(p => p.id === pageId ? { ...p, ...updates } : p));

    // AC6: Auto-adjust field widths when columns decrease
    if (updates.columns !== undefined) {
      const newCols = updates.columns;
      setFields(prev => prev.map(f => {
        if (f.pageId !== pageId) return f;
        const w = f.width || 'full';
        if (newCols === 1 && w !== 'full') {
          return { ...f, width: 'full' as const };
        }
        if (newCols === 2 && w === 'third') {
          return { ...f, width: 'half' as const };
        }
        return f;
      }));
    }
  }, [pushSnapshot]);

  const reorderPages = useCallback((fromIndex: number, toIndex: number) => {
    pushSnapshot();
    setPages(prev => {
      const sorted = [...prev].sort((a, b) => a.order - b.order);
      const [moved] = sorted.splice(fromIndex, 1);
      sorted.splice(toIndex, 0, moved);
      return sorted.map((p, i) => ({ ...p, order: i }));
    });
  }, [pushSnapshot]);

  const moveFieldToPage = useCallback((fieldId: string, pageId: string | null) => {
    pushSnapshot();
    setFields(prev => prev.map(f => {
      if (f.id !== fieldId) return f;
      // Prevent page-excluded types from being added to pages
      if (pageId && PAGE_EXCLUDED_FIELD_TYPES.includes(f.type)) return f;
      return { ...f, pageId: pageId ?? undefined };
    }));
  }, [pushSnapshot]);

  // ── Save ─────────────────────────────────────────────
  // Returns `SaveResult` so the mobile telemetry wrapper can emit
  // `save_fail` deterministically (.6.1 — TEST-001 fix). Toasts continue to
  // own the UX side; the return value carries the outcome for observers.
  const save = useCallback(async (): Promise<SaveResult> => {
    if (formId && !hasChanges) return { success: true };
    setIsSaving(true);
    try {
      if (!formId) {
        const { form: newForm, error: createError } = await createForm({
          title,
          description,
          fields,
          pages: pages.length > 0 ? pages : null,
          settings: localSettings,
        });
        if (createError || !newForm) {
          showToast('error', createError || 'Erro ao criar formulário');
          return { success: false, error: createError ?? 'create_failed' };
        }
        showToast('success', 'Formulário criado com sucesso!');
        navigate(`/dashboard/forms/${newForm.id}/edit`, { replace: true });
        return { success: true };
      }
      const result = await updateForm({
        title,
        description,
        fields,
        pages: pages.length > 0 ? pages : null,
        settings: localSettings,
      });
      if (result.error) {
        showToast('error', result.error || 'Erro ao salvar formulário');
        return { success: false, error: result.error };
      }
      showToast('success', 'Formulário salvo com sucesso!');
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      logService.error('editor.save', message, { error: err as Error, form_id: formId });
      showToast('error', 'Erro inesperado ao salvar');
      return { success: false, error: message };
    } finally {
      setIsSaving(false);
    }
  }, [formId, hasChanges, title, description, fields, pages, localSettings, createForm, updateForm, navigate, showToast]);

  // ── Ctrl+S shortcut ──────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        save();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [save]);

  // ── Publish ──────────────────────────────────────────
  const publish = useCallback(async () => {
    // Only validate when publishing (not when unpublishing)
    const isUnpublishing = formId && serverForm?.is_published;
    if (!isUnpublishing) {
      const invalidRedirect = fields.find(f => {
        if (f.type !== 'redirect_url') return false;
        const url = (f.config as any)?.url;
        return !url || !/^https?:\/\//.test(url);
      });
      if (invalidRedirect) {
        showToast('error', `O campo "${invalidRedirect.label}" precisa de uma URL válida (http:// ou https://)`);
        return;
      }
    }

    setIsSaving(true);
    try {
      if (!formId) {
        const { form: newForm, error: createError } = await createForm({
          title,
          description,
          fields,
          pages: pages.length > 0 ? pages : null,
          settings: localSettings,
          is_published: true,
        });
        if (createError || !newForm) {
          showToast('error', createError || 'Erro ao criar formulário');
          return;
        }
        showToast('success', 'Formulário publicado com sucesso!');
        navigate(`/dashboard/forms/${newForm.id}/edit`, { replace: true });
      } else {
        const newPublishState = !serverForm?.is_published;
        const result = await updateForm({
          title,
          description,
          fields,
          pages: pages.length > 0 ? pages : null,
          settings: localSettings,
          is_published: newPublishState,
        });
        if (!result.error) {
          showToast('success', newPublishState ? 'Formulário publicado!' : 'Formulário despublicado');
        } else {
          showToast('error', result.error || 'Erro ao publicar formulário');
        }
      }
    } catch (err) {
      logService.error('editor.publish', err instanceof Error ? err.message : 'Unknown error', { error: err as Error, form_id: formId });
      showToast('error', 'Erro inesperado ao publicar');
    } finally {
      setIsSaving(false);
    }
  }, [formId, title, description, fields, pages, localSettings, serverForm, createForm, updateForm, navigate, showToast]);

  // ── UI toggles ───────────────────────────────────────
  const togglePlayMode = useCallback(() => {
    setIsPlayMode(prev => !prev);
  }, []);

  const toggleViewport = useCallback(() => {
    setViewportMode(prev => prev === 'desktop' ? 'mobile' : 'desktop');
  }, []);

  const openAddFieldModal = useCallback(() => {
    setShowAddFieldModal(true);
  }, []);

  const closeAddFieldModal = useCallback(() => {
    setShowAddFieldModal(false);
  }, []);

  const toggleThemePanel = useCallback(() => {
    setShowThemePanel(prev => !prev);
  }, []);

  const openSlideSettings = useCallback(() => {
    setShowSettingsModal(true);
  }, []);

  const closeSlideSettings = useCallback(() => {
    setShowSettingsModal(false);
  }, []);

  // ── buildFormSnapshot ────────────────────────────────
  const buildFormSnapshot = useCallback((): Form => {
    return {
      id: formId || '',
      user_id: user?.id || '',
      title,
      description,
      slug: serverForm?.slug,
      fields,
      pages: pages.length > 0 ? pages : null,
      settings: localSettings,
      is_published: serverForm?.is_published ?? false,
      is_template: serverForm?.is_template ?? false,
      thank_you_message: serverForm?.thank_you_message ?? '',
      redirect_url: serverForm?.redirect_url,
      created_at: serverForm?.created_at ?? new Date().toISOString(),
      updated_at: serverForm?.updated_at ?? new Date().toISOString(),
    };
  }, [formId, user?.id, title, description, fields, pages, localSettings, serverForm]);

  // ── Memoized snapshot (avoids new object per render) ──
  const formSnapshot = useMemo(() => buildFormSnapshot(), [buildFormSnapshot]);

  // ── exit ─────────────────────────────────────────────
  const exit = useCallback(() => {
    navigate('/dashboard/forms');
  }, [navigate]);

  // ── Markdown passthrough ─────────────────────────────
  const handleMarkdownImport = useCallback((result: ParseResult, mode: 'replace' | 'append') => {
    try {
      pushSnapshot();
      if (mode === 'replace') {
        setFields(result.fields);
        if (result.title) setTitleState(result.title);
        if (result.description) setDescriptionState(result.description);
      } else {
        setFields(prev => [...prev, ...result.fields]);
      }
      if (result.fields.length > 0) {
        setSelectedFieldId(result.fields[0].id);
      }
    } catch (err) {
      logService.error('editor.markdown_import', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
      showToast('error', 'Erro ao importar Markdown');
    }
  }, [pushSnapshot, showToast]);

  const handleExportMarkdown = useCallback(() => {
    try {
      const markdown = exportFormToMarkdown(title, description, fields, localSettings);
      downloadMarkdownFile(markdown, `${title || 'formulario'}.md`);
    } catch (err) {
      logService.error('editor.markdown_export', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
      showToast('error', 'Erro ao exportar Markdown');
    }
  }, [title, description, fields, localSettings, showToast]);

  const handleCopyMarkdown = useCallback(async () => {
    try {
      const markdown = exportFormToMarkdown(title, description, fields, localSettings);
      const success = await copyMarkdownToClipboard(markdown);
      if (success) {
        showToast('success', 'Markdown copiado!');
      }
    } catch (err) {
      logService.error('editor.markdown_copy', err instanceof Error ? err.message : 'Unknown error', { error: err as Error });
      showToast('error', 'Erro ao copiar Markdown');
    }
  }, [title, description, fields, localSettings, showToast]);

  // ── Return ───────────────────────────────────────────
  return {
    // Data
    fields,
    pages,
    title,
    description,
    settings: localSettings,
    themeConfig,
    selectedFieldId,
    selectedField,
    selectedPageId,
    selectedPage,
    user,
    isPublished,

    // Loading / error
    loading,
    error,

    // State
    hasChanges,
    isSaving,
    isPlayMode,
    viewportMode,
    canUndo,
    canRedo,
    blocker,

    // UI visibility
    showAddFieldModal,
    showThemePanel,
    showSettingsModal,

    // Field CRUD
    addField,
    addFieldsBatch,
    updateField,
    updateFieldConfig,
    updateOptionScore,
    updateFieldRules,
    deleteField,
    duplicateField,
    reorderFields,
    setAlignmentAll,
    selectField,
    selectPage,

    // Page CRUD (Story 5.2)
    addPage,
    deletePage,
    updatePage,
    reorderPages,
    moveFieldToPage,

    // Form actions
    setTitle,
    setDescription,
    updateSettings,
    save,
    publish,
    exit,

    // Editor UI
    undo,
    redo,
    togglePlayMode,
    toggleViewport,
    openAddFieldModal,
    closeAddFieldModal,
    toggleThemePanel,
    openSlideSettings,
    closeSlideSettings,
    formSnapshot,
    buildFormSnapshot,

    // Markdown
    handleMarkdownImport,
    handleExportMarkdown,
    handleCopyMarkdown,
  };
}
